import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import React, { useState, useContext, useEffect, useRef } from 'react';
import type { GuideContentArticle } from '@aurora/shared-generated/types/graphql-schema-types';
import { Accordion, useClassNameMapper } from 'react-bootstrap';
import type { GuidesQuery, GuidesQueryVariables } from '../../../types/graphql-types';
import guideQuery from './../GuideSelectorComponent/Guides.query.graphql';
import GuideItem from './GuideItem/GuideItem';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import SideNavigationWithMobileOverlay from '../SideNavigationWithMobileOverlay/SideNavigationWithMobileOverlay';
import { extractGuideContentArticleData } from '../../tkbs/TkbArticleWidget/GuideNavigationHelper';

interface Props {
  /**
   * Class name(s) to apply to the wrapping element.
   */
  className?: string;
  /**
   * Associated chapters or guide for an article
   */
  guideContentArticle: GuideContentArticle;
}

/**
 * Guide navigation
 *
 * @author Amit Agrawal
 */
const GuideNavigation: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  guideContentArticle
}) => {
  const cx = useClassNameMapper();

  const [selectedGuide, chapter] = extractGuideContentArticleData(guideContentArticle);
  const nodeId = useRef<string>(selectedGuide?.node?.id);

  const initialToggleValue = {
    [selectedGuide?.id]: true,
    ...(chapter && { [chapter?.id]: true })
  };

  const [isActiveToggle, setActiveToggle] =
    useState<{ [key: string]: boolean }>(initialToggleValue);

  const {
    publicConfig: { maxGuidesAndContentLimit }
  } = useContext(TenantContext);

  const variables: GuidesQueryVariables = {
    constraints: {
      nodeId: {
        eq: nodeId.current
      },
      includeEmpty: { eq: false }
    },
    useCount: true,
    useFirstArticle: false,
    first: maxGuidesAndContentLimit
  };

  const { data: guideData, loading: guideLoading } = useQueryWithTracing<
    GuidesQuery,
    GuidesQueryVariables
  >(module, guideQuery, {
    variables
  });

  /**
   * useEffect hook to set the active state to `true` of a selected guide or chapter.
   */
  useEffect(() => {
    if (selectedGuide || chapter) {
      setActiveToggle(previous => ({
        ...previous,
        [selectedGuide.id]: true,
        ...(chapter && { [chapter.id]: true })
      }));
    }
  }, [selectedGuide, chapter]);

  if (guideLoading || Object.keys(isActiveToggle).length === 0 || !guideData) {
    return null;
  }

  const { guides } = guideData;

  /**
   * Toggle items value on accordion state change
   * @param id
   */
  const toggleItem = (id: string) => {
    setActiveToggle(previousState => ({
      ...previousState,
      [id]: !Boolean(previousState[id])
    }));
  };

  return (
    <SideNavigationWithMobileOverlay buttonTitle={selectedGuide?.title} className={className}>
      {guides &&
        guides.edges.map(({ node }) => (
          <Accordion
            key={node.id}
            className={cx('d-flex flex-column')}
            activeKey={isActiveToggle[node.id] ? node.id : undefined}
          >
            <GuideItem
              guide={node}
              isActiveToggle={isActiveToggle}
              toggleItem={toggleItem}
              selectedGuideId={selectedGuide?.id}
            />
          </Accordion>
        ))}
    </SideNavigationWithMobileOverlay>
  );
};

export default GuideNavigation;
